import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

interface FilePermissionVisibility {
  convert_original: boolean;
  convert_original_after?: string; // ISO 8601 timestamp
  save_changes: boolean;
  save_changes_after?: string;
  copy_paste: boolean;
  copy_paste_after?: string;
  screen_capture: boolean;
  screen_capture_after?: string;
  screen_watermark: boolean;
  screen_watermark_after?: string;
  print: boolean;
  print_after?: string;
  print_watermark: boolean;
  print_watermark_after?: string;
  share: boolean;
  share_after?: string;
}

interface FolderPermissionVisibility {
  create: boolean;
  create_after?: string; // ISO 8601 timestamp
  copy: boolean;
  copy_after?: string;
  add: boolean;
  add_after?: string;
  move: boolean;
  move_after?: string;
  share: boolean;
  share_after?: string;
  delete: boolean;
  delete_after?: string;
}

interface CompanyPolicySettings {
  file_permissions?: FilePermissionVisibility;
  folder_permissions?: FolderPermissionVisibility;
}

export const useCompanyPolicySettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<CompanyPolicySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.company_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const policySettings = await apiService.getCompanyPolicySettings(user.company_id);
        
        const normalizedSettings: CompanyPolicySettings = {
          ...policySettings,
        };
        
        setSettings(normalizedSettings);
      } catch (err: any) {
        console.error('Failed to load company policy settings:', err);
        setError('Failed to load company policy settings');
        // Default to all permissions visible if we can't load settings
        setSettings({
          file_permissions: {
            convert_original: true,
            save_changes: true,
            copy_paste: true,
            screen_capture: true,
            screen_watermark: true,
            print: true,
            print_watermark: true,
            share: true,
          },
          folder_permissions: {
            create: true,
            copy: true,
            add: true,
            move: true,
            share: true,
            delete: true,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user?.company_id]);

  // Helper function to check if a file permission is visible
  // If createdAt is provided and apply_after is set, checks if the permission should be visible based on the cutoff
  const isFilePermissionVisible = (permission: string, createdAt?: string | Date): boolean => {
    if (!settings?.file_permissions) return true; // Default to visible if not set
    
    // Get the boolean visibility value (exclude *_after fields)
    const permissionKey = permission as keyof FilePermissionVisibility;
    const visibleValue = settings.file_permissions[permissionKey];
    // Type guard: ensure we have a boolean value, not a string (which would be an *_after field)
    // Explicitly handle false values - if it's explicitly false, return false. Otherwise default to true.
    let visible: boolean;
    if (typeof visibleValue === 'boolean') {
      visible = visibleValue; // Use the actual boolean value (true or false)
    } else {
      visible = true; // Default to true if undefined or not a boolean
    }
    
    const applyAfterKey = `${permission}_after` as keyof FilePermissionVisibility;
    const applyAfter = settings.file_permissions[applyAfterKey] as string | undefined;
    
    // If apply_after is set and createdAt is provided, check the cutoff
    if (applyAfter && createdAt) {
      const applyAfterDate = new Date(applyAfter);
      const createdAtDate = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
      
      // If the policy/asset was created before apply_after, use the opposite visibility
      if (createdAtDate < applyAfterDate) {
        return !visible;
      }
      // Otherwise, use the current visibility (created after apply_after)
      return visible;
    }
    
    // No apply_after or no createdAt provided: use the visibility setting directly
    return visible;
  };

  // Helper function to check if a folder permission is visible
  // If createdAt is provided and apply_after is set, checks if the permission should be visible based on the cutoff
  const isFolderPermissionVisible = (permission: string, createdAt?: string | Date): boolean => {
    if (!settings?.folder_permissions) return true; // Default to visible if not set
    
    // Get the boolean visibility value (exclude *_after fields)
    const permissionKey = permission as keyof FolderPermissionVisibility;
    const visibleValue = settings.folder_permissions[permissionKey];
    // Type guard: ensure we have a boolean value, not a string (which would be an *_after field)
    const visible = typeof visibleValue === 'boolean' ? visibleValue : true;
    
    const applyAfterKey = `${permission}_after` as keyof FolderPermissionVisibility;
    const applyAfter = settings.folder_permissions[applyAfterKey] as string | undefined;
    
    // If apply_after is set and createdAt is provided, check the cutoff
    if (applyAfter && createdAt) {
      const applyAfterDate = new Date(applyAfter);
      const createdAtDate = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
      
      // If the policy/asset was created before apply_after, use the opposite visibility
      if (createdAtDate < applyAfterDate) {
        return !visible;
      }
      // Otherwise, use the current visibility (created after apply_after)
      return visible;
    }
    
    // No apply_after or no createdAt provided: use the visibility setting directly
    return visible;
  };

  return {
    settings,
    loading,
    error,
    isFilePermissionVisible,
    isFolderPermissionVisible,
  };
};

